import { useEffect } from 'react';
import Layout from '../components/Layout';
import { Home, CheckCircle2, Clock, Sparkles, Heart, Percent, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCmsContent, parsePoints } from '../context/CmsImagesContext';

export default function PrivateCleaning() {
  const { t, language } = useLanguage();
  const { getImage, getText, getHtml } = useCmsContent();

  useEffect(() => {
    document.title = t({ da: "FTR Service ApS - Privatrengøring", en: "FTR Service ApS - Private Cleaning" });
  }, [t]);

  // Forbered tilbudspunkter
  const benefitIcons = [
    <Clock className="w-6 h-6" />,
    <Sparkles className="w-6 h-6" />,
    <Home className="w-6 h-6" />,
    <Heart className="w-6 h-6" />,
    <ShieldCheck className="w-6 h-6" />
  ];

  // 1. Tjek individuelle tilbud_punkt_1..5 felter fra CMS (mest robuste løsning for kunden)
  const defaultBenefitList = [
    { 
      title: t({ da: 'Regelmæssig Rengøring', en: 'Regular Cleaning' }), 
      text: t({ da: 'Ugentlig, hver 14. dag eller månedligt – helt efter dit ønske.', en: 'Weekly, every 14 days or monthly – exactly as you wish.' }), 
      icon: <Clock className="w-6 h-6" /> 
    },
    { 
      title: t({ da: 'Hovedrengøring', en: 'Deep Cleaning' }), 
      text: t({ da: 'Når der skal gøres ekstra grundigt rent i alle kroge og kanter.', en: 'When extra thorough cleaning is needed in every nook and cranny.' }), 
      icon: <Sparkles className="w-6 h-6" /> 
    },
    { 
      title: t({ da: 'Flytterengøring', en: 'Move-out Cleaning' }), 
      text: t({ da: 'Professionel rengøring ved fraflytning eller indflytning.', en: 'Professional cleaning when moving out or moving in.' }), 
      icon: <Home className="w-6 h-6" /> 
    },
    { 
      title: t({ da: 'Tryghed & Tillid', en: 'Trust & Safety' }), 
      text: t({ da: 'Vi passer på dit hjem og dine ejendele, som var det vores egne.', en: 'We take care of your home and belongings as if they were our own.' }), 
      icon: <Heart className="w-6 h-6" /> 
    },
    { 
      title: t({ da: 'Skræddersyede aftaler', en: 'Tailored agreements' }), 
      text: t({ da: 'Vi tilpasser altid rengøringen efter dine specifikke behov og personlige ønsker.', en: 'We always tailor the cleaning to your specific needs and personal wishes.' }), 
      icon: <ShieldCheck className="w-6 h-6" /> 
    }
  ];

  const individualBenefits = [];
  for (let i = 1; i <= 5; i++) {
    const def = defaultBenefitList[i - 1];
    const title = getText('privatrengoring', `tilbud_punkt_${i}_titel`, getText('privat_tilbud', `punkt_${i}_titel`, def ? def.title : ''));
    const text = getText('privatrengoring', `tilbud_punkt_${i}_tekst`, getText('privat_tilbud', `punkt_${i}_tekst`, def ? def.text : ''));
    if (title || text) {
      individualBenefits.push({
        title,
        text,
        icon: benefitIcons[(i - 1) % benefitIcons.length],
      });
    }
  }

  let benefits = individualBenefits;

  // 2. Hvis ikke sat via individuelle felter, parse tilbud_punkter
  if (benefits.length === 0) {
    const tilbudPunkterRaw = getText('privatrengoring', 'tilbud_punkter', getText('privat_tilbud', 'punkter', ''));
    const parsedBenefits = parsePoints(tilbudPunkterRaw, language);
    if (parsedBenefits.length > 0) {
      benefits = parsedBenefits.map((pt, idx) => ({
        title: pt.title,
        text: pt.text || pt.full,
        icon: benefitIcons[idx % benefitIcons.length],
      }));
    }
  }

  // 3. Fallback til standard punkter
  if (benefits.length === 0) {
    benefits = defaultBenefitList;
  }

  // Forbered tjeklistepunkter (både fra richtext og multiline tekst)
  const defaultChecklistItems = [
    t({ da: 'Grundig støvsugning af alle rum', en: 'Thorough vacuuming of all rooms' }),
    t({ da: 'Vask af alle gulvflader', en: 'Washing of all floor surfaces' }),
    t({ da: 'Aftørring af alle vandrette flader', en: 'Wiping of all horizontal surfaces' }),
    t({ da: 'Rengøring af badeværelse (toilet, vask, bad)', en: 'Cleaning of bathroom (toilet, sink, bath)' }),
    t({ da: 'Udvendig aftørring af køkkenskabe', en: 'External wiping of kitchen cabinets' }),
    t({ da: 'Rengøring af køkkenvask og armatur', en: 'Cleaning of kitchen sink and faucet' }),
    t({ da: 'Aftørring af døre, paneler og kontakter', en: 'Wiping of doors, panels and switches' }),
    t({ da: 'Vinduespudsning (efter aftale)', en: 'Window cleaning (by agreement)' })
  ];

  const tjeklisteTekst = getHtml('privatrengoring', 'tjekliste_tekst', getHtml('privat_tjekliste', 'tekst', ''));
  const tjeklistePunkterRaw = getText('privatrengoring', 'tjekliste_punkter', getText('privat_tjekliste', 'punkter', ''));
  const parsedChecklist = parsePoints(tjeklisteTekst || tjeklistePunkterRaw, language);
  let checklistItems = [];
  if (parsedChecklist.length > 0) {
    checklistItems = parsedChecklist.map((pt) => pt.full || pt.text);
  }

  if (checklistItems.length === 0 || (language === 'en' && parsedChecklist.length > 0 && parsedChecklist.every(p => !p.text || /[\u00C0-\u017F]/.test(p.text) || /og|af|alle/.test(p.text)))) {
    checklistItems = defaultChecklistItems;
  }

  // Forbered CTA-citat (renset for redundante anførselstegn, så det altid formateres fejlfrit)
  const rawCitat = getText('privatrengoring', 'cta_citat', getText('privat_cta', 'citat', t({ da: "Vi skaber rene rammer, så du kan skabe minder.", en: "We create clean surroundings so you can create memories." })));
  const cleanCitat = typeof rawCitat === 'string' ? rawCitat.replace(/^["'“”«»]+|["'“”«»]+$/g, '').trim() : rawCitat;

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={getImage('privat_hero_billede', getImage('private_hero', '/images/private-cleaning.png', { width: 1400 }))} 
            alt="" 
            className="w-full h-full object-cover opacity-30" 
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide uppercase bg-brand-primary/10 text-brand-primary mb-6">
              {getText('privatrengoring', 'hero_underoverskrift', getText('privat_hero', 'underoverskrift', t({ da: "Privatrengøring", en: "Private Cleaning" })))}
            </span>
            <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              {getText('privatrengoring', 'hero_overskrift', getText('privat_hero', 'overskrift', t({ da: "Få mere tid til det vigtigste i din hverdag", en: "Get more time for what matters most in your everyday life" })))}
            </h1>
            {getText('privatrengoring', 'hero_tekst', getText('privat_hero', 'tekst', null)) ? (
              <div 
                className="text-xl text-gray-600 mb-10 leading-relaxed [&>p]:mb-4"
                dangerouslySetInnerHTML={{ __html: getHtml('privatrengoring', 'hero_tekst', getHtml('privat_hero', 'tekst', t({
                  da: '<p>Privatrengøring hjælper med faste ugentlige eller månedlige besøg, hovedrengøring og flytterengøring. Det giver mere tid til hverdagens vigtigste gøremål, mens hjemmet altid fremstår rent og velholdt.</p>',
                  en: '<p>Private cleaning helps with fixed weekly or monthly visits, deep cleaning and move-out cleaning. It gives more time for everyday life\'s most important tasks, while the home always appears clean and well-maintained.</p>'
                }))) }}
              />
            ) : (
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                {t({ 
                  da: "Privatrengøring hjælper med faste ugentlige eller månedlige besøg, hovedrengøring og flytterengøring. Det giver mere tid til hverdagens vigtigste gøremål, mens hjemmet altid fremstår rent og velholdt.",
                  en: "Private cleaning helps with fixed weekly or monthly visits, deep cleaning and move-out cleaning. It gives more time for everyday life's most important tasks, while the home always appears clean and well-maintained."
                })}
              </p>
            )}
            <div className="flex flex-wrap gap-4">
              <Link 
                to={getText('privatrengoring', 'hero_knap_link', getText('privat_hero', 'knap_link', '/kontakt'))} 
                className="bg-[#85d7f9] text-white px-10 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all shadow-xl shadow-[#85d7f9]/20"
              >
                {getText('privatrengoring', 'hero_knap_tekst', getText('privat_hero', 'knap_tekst', t({ da: "Book et besøg", en: "Book a visit" })))}
              </Link>
              <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm px-6 py-4 rounded-2xl border border-sky-100 shadow-lg">
                <div className="bg-sky-500 text-white p-2 rounded-lg">
                  <Percent className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 leading-tight">
                    {t({ da: "Spar 26% med", en: "Save 26% with" })}
                  </p>
                  <p className="text-xs text-gray-600">
                    {t({ da: "Servicefradrag", en: "Service deduction" })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Servicefradrag Info */}
      <section className="py-12 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-[2rem] p-8 md:p-12 shadow-xl border border-sky-100 flex flex-col md:flex-row items-center gap-8">
            <div className="bg-sky-100 p-6 rounded-3xl">
              <Percent className="w-12 h-12 text-sky-600" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                {getText('privatrengoring', 'fradrag_overskrift', getText('privat_fradrag', 'overskrift', t({ da: "Vidste du, at du kan spare 26%?", en: "Did you know you can save 26%?" })))}
              </h2>
              {getText('privatrengoring', 'fradrag_tekst', getText('privat_fradrag', 'tekst', null)) ? (
                <div 
                  className="text-lg text-gray-600 leading-relaxed [&>p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: getHtml('privatrengoring', 'fradrag_tekst', getHtml('privat_fradrag', 'tekst', t({
                    da: '<p>Som privatperson kan du benytte dit servicefradrag til rengøring i hjemmet. Det betyder, at du reelt sparer ca. 26% af den samlede regning gennem din årsopgørelse.</p>',
                    en: '<p>As a private individual, you can use your service deduction for home cleaning. This means you effectively save approx. 26% of the total bill through your tax assessment.</p>'
                  }))) }}
                />
              ) : (
                <p className="text-lg text-gray-600 leading-relaxed">
                  {t({ 
                    da: "Som privatperson kan du benytte dit servicefradrag til rengøring i hjemmet. Det betyder, at du reelt sparer ca. 26% af den samlede regning gennem din årsopgørelse.",
                    en: "As a private individual, you can use your service deduction for home cleaning. This means you effectively save approx. 26% of the total bill through your tax assessment."
                  })}
                </p>
              )}
            </div>
            <div className="shrink-0">
              <a 
                href={getText('privatrengoring', 'fradrag_knap_link', getText('privat_fradrag', 'knap_link', 'https://skat.dk/borger/fradrag/servicefradrag'))} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sky-600 font-bold hover:underline"
              >
                {getText('privatrengoring', 'fradrag_knap_tekst', getText('privat_fradrag', 'knap_tekst', t({ da: "Læs mere hos SKAT", en: "Read more at SKAT" })))}
                <span className="text-xl">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-[#85d7f9]/20 rounded-3xl -z-0"></div>
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#85d7f9]/10 rounded-full blur-2xl -z-0"></div>
                
                <img 
                  src={getImage('privat_tilbud_billede', getImage('private_kitchen', '/images/kitchen-cleaning.png', { width: 900 }))} 
                  alt={t({ da: "Rent køkken", en: "Clean kitchen" })} 
                  className="rounded-3xl shadow-2xl relative z-10" 
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                {getText('privatrengoring', 'tilbud_overskrift', getText('privat_tilbud', 'overskrift', t({ da: "Hvad vi tilbyder i dit hjem", en: "What we offer in your home" })))}
              </h2>
              <ul className="space-y-6">
                {benefits.map((benefit, idx) => (
                  <li key={idx} className="flex gap-4">
                    <div className="bg-white text-brand-primary p-3 rounded-xl shadow-sm border border-gray-100 shrink-0 h-fit">
                      {benefit.icon}
                    </div>
                    <div>
                      {benefit.title && <h4 className="font-bold text-gray-900 mb-1">{benefit.title}</h4>}
                      <p className="text-gray-600">{benefit.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Checklist Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 bg-white border border-gray-100 shadow-xl rounded-[3rem] p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-gray-900">
              {getText('privatrengoring', 'tjekliste_overskrift', getText('privat_tjekliste', 'overskrift', t({ da: "Vores standard tjekliste", en: "Our standard checklist" })))}
            </h3>
            <p className="text-gray-500 mt-2">
              {getText('privatrengoring', 'tjekliste_underoverskrift', getText('privat_tjekliste', 'underoverskrift', t({ da: "Dette er inkluderet i en standard privatrengøring", en: "This is included in a standard private cleaning" })))}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6">
            {checklistItems.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-brand-primary shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-8 border-t border-gray-100 text-center">
            {cleanCitat && (
              <p className="text-gray-600 mb-8 italic">
                "{cleanCitat}"
              </p>
            )}
            <Link 
              to={getText('privatrengoring', 'cta_knap_link', getText('privat_cta', 'knap_link', '/kontakt'))} 
              className="bg-[#85d7f9] text-white px-8 py-3 rounded-xl font-bold hover:opacity-90 transition-all shadow-lg shadow-[#85d7f9]/20 inline-block"
            >
              {getText('privatrengoring', 'cta_knap_tekst', getText('privat_cta', 'knap_tekst', t({ da: "Bestil et uforpligtende tilbud", en: "Order a non-binding quote" })))}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
